import React, { useState, useRef, useEffect } from "react";
import { getLabelDrawerKey, getLabelDrawerOpen, setLabelDrawerOpen } from "./drawerState.js";
import { MarkerPanel } from "./MarkerPanel.js";
import { LimitsPanel } from "./LimitsPanel.js";
import { TextPanel } from "./TextPanel.js";
import { getPlainLabelLineText } from "./labelTextDisplay.js";

function LabelTextDropdown(props) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Initialize selected index from sessionStorage or default to 0
  const getStoredIndex = () => {
    if (!props.labelId) return 0;
    const stored = sessionStorage.getItem(`labelTextDropdown_${props.labelId}`);
    const index = stored ? parseInt(stored, 10) : 0;
    // Ensure index is within bounds
    return index < props.text.length ? index : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(getStoredIndex());

  // Update selected index if text array changes and current index is out of bounds
  useEffect(() => {
    if (selectedIndex >= props.text.length && props.text.length > 0) {
      setSelectedIndex(0);
      if (props.labelId) {
        sessionStorage.setItem(`labelTextDropdown_${props.labelId}`, '0');
      }
    }
  }, [props.text.length, selectedIndex, props.labelId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleSelectLine = (index, e) => {
    e.stopPropagation();
    setSelectedIndex(index);
    setIsOpen(false);
    // Persist to sessionStorage
    if (props.labelId) {
      sessionStorage.setItem(`labelTextDropdown_${props.labelId}`, index.toString());
    }
  };

  const selectedText = props.text.length === 0 ? "" : getPlainLabelLineText(props.text[selectedIndex]);
  const displayText = props.text.length === 0 
    ? "No text lines" 
    : (selectedText || "(empty)");

  return (
    <div ref={dropdownRef} className="LabelTextDropdownContainer">
      <div className="LabelTextDropdown" onClick={handleClick} title="Text lines">
        <span className="LabelTextDropdownDisplay">{displayText}</span>
        <i className="material-icons notranslate LabelTextDropdownArrow">
          {isOpen ? "arrow_drop_up" : "arrow_drop_down"}
        </i>
      </div>
      {isOpen && (
        <div className="LabelTextDropdownList">
          {props.text.length === 0 ? (
            <div className="LabelTextDropdownEntry disabled">No text lines</div>
          ) : (
            props.text.map((line, index) => (
              <div
                key={index}
                className={`LabelTextDropdownEntry${index === selectedIndex ? ' selected' : ''}`}
                onClick={(e) => handleSelectLine(index, e)}
              >
                <span className="LabelTextDropdownEntryNumber">{index + 1}</span>
                <span className="LabelTextDropdownEntryText">{getPlainLabelLineText(line) || "(empty)"}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ScreenLabelHeader(props) {
  const handleFind = (e) => {
    e.stopPropagation();
    Inventum.labels.zoomTo(props.id);
  };

  const handleLocation = (e) => {
    e.stopPropagation();
    Inventum.labels.locationControl(props.id);
    props.getLabels();
  };

  const handleClone = (e) => {
    e.stopPropagation();
    Inventum.labels.duplicate(props.id);
    props.getLabels();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    Inventum.labels.delete(props.id);
    props.getLabels();
  };

  const handleToggleSceneState = (e) => {
    e.stopPropagation();
    Inventum.labels.toggle2DSceneStateable(props.id);
    props.getLabels();
  };

  const handleToggleVisibleStart = (e) => {
    e.stopPropagation();
    Inventum.labels.toggleVisibleStart(props.id);
    props.getLabels();
  };

  const handleVisible = (e) => {
    e.stopPropagation();
    Inventum.labels.toggleVisible(props.id);
    props.getLabels();
  };

  return (
    <div onClick={props.toggleOpen} className="LabelHeader">
      <span className="PanelOpenStatus">{props.isOpen ? "▾" : "▸"}</span>
      <span className="LabelHeaderBtn delete" onClick={handleDelete} title="Delete Label">
        <i className="material-icons notranslate">clear</i>
      </span>
      <span className="LabelHeaderBtn" onClick={handleClone} title="Clone Label">
        <i className="material-icons notranslate">copy_all</i>
      </span>
      <span className="LabelHeaderBtn" onClick={handleFind} title="Find Label">
        <i className="material-icons notranslate">adjust</i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.transformActive ? " active" : "")}
        onClick={handleLocation}
        title="Move Label (Esc to hide)"
      >
        <i className="material-icons notranslate">open_with</i>
      </span>
      <span
        className="LabelHeaderBtn"
        style={{ opacity: props.isSceneStateable ? 1.0 : 0.4 }}
        onClick={handleToggleSceneState}
        title={
          props.isSceneStateable
            ? "Label will appear in Scene States"
            : "Label is excluded from Scene States (Legacy)"
        }
      >
        <i className="material-icons notranslate">
          {props.isSceneStateable ? "task" : "not_interested"}
        </i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.visibleStart ? " active" : "")}
        style={{ opacity: props.visibleStart ? 1.0 : 0.4 }}
        onClick={handleToggleVisibleStart}
        title={
          props.visibleStart
            ? "Label will appear when Scene Loads"
            : "Label won't appear when Scene Loads"
        }
      >
        <i className="material-icons notranslate">
          {props.visibleStart ? "tour" : "not_interested"}
        </i>
      </span>
      <span
        className={"LabelHeaderBtn" + (props.visible ? " active" : "")}
        onClick={handleVisible}
        title="Toggle Visible"
      >
        <i className="material-icons notranslate">
          {props.visible ? "check_box" : "check_box_outline_blank"}
        </i>
      </span>
      <div className="LabelTextPillContainer">
        <LabelTextDropdown text={props.text} labelId={props.id} />
      </div>
    </div>
  );
}

function ScreenLabelDrawer(props) {
  return (
    <div className="LabelDrawerContent">
      <TextPanel {...props} />
      <MarkerPanel {...props} />
      <LimitsPanel {...props} />
    </div>
  );
}

// Convert the class component to a functional component
function ScreenLabel(props) {
  const labelDrawerKey = props.labelDrawerKey || getLabelDrawerKey(props);
  const [open, setOpen] = useState(() => getLabelDrawerOpen(labelDrawerKey));

  const toggleOpen = () => {
    setOpen((currentOpen) => {
      const nextOpen = !currentOpen;
      setLabelDrawerOpen(labelDrawerKey, nextOpen);
      return nextOpen;
    });
  };

  return (
    <div className="LabelItem">
      <ScreenLabelHeader toggleOpen={toggleOpen} isOpen={open} {...props} />
      {open ? (
        <div className="LabelItemDrawer">
          <ScreenLabelDrawer {...props} />
        </div>
      ) : null}
    </div>
  );
}

export { ScreenLabel };
